import { AuditEventList, AuditFilters } from "@/components/audit/AuditEvents";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { type AuditQuery, api } from "@/lib/api";
import { ORG_AUDIT_CATEGORIES } from "@/lib/audit-copy";
import { type AuditSearch, EXPORT_MAX_DAYS, auditQueryOf, currentMinute, exportWindow, hasAuditFilters } from "@/lib/audit-search";
import { useSuperAdminView } from "@/lib/use-superadmin-view";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Download } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";

// The org's Audit log: every recorded action across its Boos, people and
// settings, Boo Lab support's visits included. Org owners and admins (and
// Boo Lab staff) see it. Filters live in the URL (lib/audit-search.ts).

const PAGE_SIZE = 50;

export function AuditLogPage() {
  useDocumentTitle("Audit log");
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const search = useSearch({ strict: false }) as AuditSearch;
  const navigate = useNavigate();
  const staffView = useSuperAdminView();
  const now = currentMinute();

  const setSearch = (patch: Partial<AuditSearch>) =>
    navigate({
      to: "/orgs/$orgId/audit",
      params: { orgId },
      search: (prev: AuditSearch) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
        return next;
      },
      replace: true,
    } as never);

  const perms = useQuery({ queryKey: ["org-permissions", orgId], queryFn: api.org.permissions });
  const boos = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const org = useQuery({ queryKey: ["org-full", orgId], queryFn: () => api.org.details() });

  const query = useMemo(() => auditQueryOf(search, now), [search, now]);
  const events = useInfiniteQuery({
    queryKey: ["org-audit", orgId, query],
    queryFn: ({ pageParam }) => api.org.audit.list({ ...query, before: pageParam ?? undefined, limit: PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextBefore,
    enabled: perms.data?._permissions?.canViewAudit !== false,
  });

  if (perms.data?._permissions?.canViewAudit === false) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <PageHeader title="Audit log" />
        <p className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          Only organization owners and admins can see the audit log.
        </p>
      </div>
    );
  }

  const orgBoos = (boos.data?.data ?? []).filter((b) => b.organizationId === orgId);
  const members = org.data?.data.members ?? [];

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader title="Audit log" subtitle="Who did what across your Boos, people and settings, including every visit from Boo Lab support." />
      <section className="flex flex-col gap-4" aria-label="Audit events">
        <AuditFilters
          search={search}
          onChange={setSearch}
          categories={ORG_AUDIT_CATEGORIES}
          boos={orgBoos}
          members={members}
          end={
            <>
              {staffView && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Switch checked={!!search.internal} onCheckedChange={(on) => setSearch({ internal: on ? true : undefined })} aria-label="Include Boo Lab-only events" />
                  Include Boo Lab-only events
                </label>
              )}
              <ExportMenu query={query} now={now} />
            </>
          }
        />
        <AuditEventList
          events={events}
          filtered={hasAuditFilters(search)}
          showBoo
          emptyText="Nothing recorded yet. Changes and sign-ins show up here as they happen."
        />
      </section>
    </div>
  );
}

function ExportMenu({ query, now }: { query: AuditQuery; now: number }) {
  const span = exportWindow(query, now);
  const download = useMutation({
    mutationFn: (format: "csv" | "jsonl") => api.org.audit.export(format, { ...query, from: span.from, to: span.to }).then((blob) => ({ blob, format })),
    onSuccess: ({ blob, format }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `boo-audit-log-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: () => toast.error("Couldn't export the audit log. Try again."),
  });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" loading={download.isPending}>
          <Download />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {span.trimmed ? `The file holds the last ${EXPORT_MAX_DAYS} days of what's shown.` : "The file holds everything these filters show."}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => download.mutate("csv")}>CSV, for spreadsheets</DropdownMenuItem>
        <DropdownMenuItem onClick={() => download.mutate("jsonl")}>JSON Lines, for log tools</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
